// Platform types
export type Platform = "web" | "ios" | "android";
export type Duration = "monthly" | "annually";
export type SubscriptionStatus = "active" | "inactive" | "draft";

// Platform-specific pricing configuration
export interface PlatformPricing {
  platform: Platform;
  enabled: boolean;
  pricing: {
    [key in Duration]?: {
      enabled: boolean;
      price: number;
      currency: string;
      subscriptionId?: string;
      stripeProductId?: string;
      stripePriceId?: string;
      appStoreProductId?: string;
      googlePlayProductId?: string;
    };
  };
}

// Feature configuration
export interface FeatureConfiguration {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  value?: string | number | boolean;
}

// Limits configuration - Removed the specified fields
export interface LimitsConfiguration {
  storageGB: number | "unlimited";

  minutesPerMonth: number | "unlimited";
  summariesPerMonth: number | "unlimited";
  maxDurationMinutes: number | "unlimited";
}

// Main subscription plan interface
export interface SubscriptionPlan {
  id: string;
  uuid?: string;
  name: string;
  description: string;
  status: SubscriptionStatus;
  isActive: boolean;
  is_popular: boolean;
  trial_days: number;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;

  // Platform-specific configurations
  platforms: PlatformPricing[] | null;

  // Features and limits
  features: FeatureConfiguration[];
  limits: LimitsConfiguration & { uploadLimits?: any };
  subscribers_count: number;
  total_revenue: number;
  // Display options
  sort_order?: number;
  colorScheme?: string;
  icon?: string;
  default_speech_to_text_llm_model_id?: string;
  default_completion_llm_model_id?: string;
}

// Statistics for overview cards
export interface SubscriptionStats {
  totalPlans: number;
  activePlans: number;
  totalSubscribers: number;
  monthlyRevenue: number;
  annualRevenue: number;
  averageRevenuePerUser: number;
  conversionRate: number;
  churnRate: number;
  planBreakdown: {
    planId: string;
    planName: string;
    subscribers: number;
    revenue: number;
    percentage: number;
  }[];
  platformBreakdown: {
    platform: Platform;
    subscribers: number;
    revenue: number;
    percentage: number;
  }[];
  revenueByDuration: {
    monthly: number;
    annually: number;
  };
}

// Filter options for subscription management
export interface SubscriptionFilters {
  search?: string;
  status?: SubscriptionStatus | "all";
  platform?: Platform | "all";
}

// Sort options
export type SubscriptionSortField =
  | "name"
  | "status"
  | "createdAt"
  | "updatedAt"
  | "sortOrder"
  | "sort_order";
export type SortDirection = "asc" | "desc";

export interface SubscriptionSort {
  field: SubscriptionSortField;
  direction: SortDirection;
}

// API request/response types
export interface CreateSubscriptionPlanRequest {
  name: string;
  description: string;
  platforms: PlatformPricing[] | null;
  features: FeatureConfiguration[];
  limits: LimitsConfiguration & { uploadLimits?: any };
  isActive?: boolean;
  isPopular?: boolean;
  trialDays?: number;
  sortOrder?: number;
  sort_order?: number;
  default_speech_to_text_llm_model_id?: string;
  default_completion_llm_model_id?: string;
}

export interface UpdateSubscriptionPlanRequest {
  id: string;
  name?: string;
  description?: string;
  platforms?: PlatformPricing[] | null;
  features?: FeatureConfiguration[];
  limits?: LimitsConfiguration & { uploadLimits?: any };
  status?: SubscriptionStatus;
  isActive?: boolean;
  isPopular?: boolean;
  trialDays?: number;
  sortOrder?: number;
  sort_order?: number;
  default_speech_to_text_llm_model_id?: string;
  default_completion_llm_model_id?: string;
}

export interface SubscriptionPlansResponse {
  plans: SubscriptionPlan[];
  total: number;
  page: number;
  limit: number;
}

// Redux state
export interface SubscriptionState {
  plans: SubscriptionPlan[];
  stats: SubscriptionStats | null;
  isLoading: boolean;
  error: string | null;
  filters: SubscriptionFilters;
  sort: SubscriptionSort;
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

// User subscription (for reference, used in user management)
export interface UserSubscription {
  id: string;
  userId: string;
  planId: string;
  platform: Platform;
  duration: Duration;
  status: "active" | "cancelled" | "expired" | "trial" | "past_due";
  startDate: string;
  endDate: string;
  autoRenew: boolean;
  paymentMethod?: string;
  amount: number;
  currency: string;
  trialUsed: boolean;
  stripeSubscriptionId?: string;
  createdAt: string;
  updatedAt: string;
}

// Default feature configurations for different plan names - Updated structure
export const DEFAULT_FEATURES: {
  [planName: string]: FeatureConfiguration[];
} = {
  "Free Plan": [
    {
      id: "basic_summaries",
      name: "Unlimited recordings and transcriptions",
      description: "Unlimited recordings and transcriptions",
      enabled: true,
    },
    {
      id: "pdf_export",
      name: "Standard AI models",
      description: "Standard AI models",
      enabled: true,
    },
    {
      id: "feature_1750601333476",
      name: "2GB cloud storage",
      description: "2GB cloud storage",
      enabled: true,
    },
    {
      id: "feature_1750601344353",
      name: "Basic export options (Copy and Text)",
      description: "Basic export options (Copy and Text)",
      enabled: true,
    },
    {
      id: "feature_1750601348798",
      name: "Text Summary",
      description: "Text Summary",
      enabled: true,
    },
  ],
  "Personal Plan": [
    {
      id: "basic_summaries",
      name: "Unlimited recordings and transcriptions",
      description: "Unlimited recordings and transcriptions",
      enabled: true,
    },
    {
      id: "advanced_summaries",
      name: "Advanced AI models",
      description: "Advanced AI models",
      enabled: true,
    },
    {
      id: "multiple_exports",
      name: "Text and File upload for summary",
      description: "Text and File upload for summary",
      enabled: true,
    },
    {
      id: "api_access",
      name: "Regenerate Summary",
      description: "Regenerate Summary",
      enabled: true,
    },
    {
      id: "priority_support",
      name: "Multiple export formats (Copy, text, PDF and Word)",
      description: "Multiple export formats (Copy, text, PDF and Word)",
      enabled: true,
    },
    {
      id: "feature_1750601972845",
      name: "Advanced summarization styles",
      description: "Advanced summarization styles",
      enabled: true,
    },
    {
      id: "feature_1750601984035",
      name: "Priority customer support",
      description: "Priority customer support",
      enabled: true,
    },
  ],
  "Premium Plan": [
    {
      id: "feature_1750602168863",
      name: "Unlimited recordings and transcriptions",
      description: "Unlimited recordings and transcriptions",
      enabled: true,
    },
    {
      id: "feature_1750602189819",
      name: "Advanced AI models",
      description: "Advanced AI models",
      enabled: true,
    },
    {
      id: "feature_1750602199894",
      name: "Unlimited minutes",
      description: "Unlimited minutes",
      enabled: true,
    },
    {
      id: "feature_1750602212692",
      name: "Text and File upload for summary",
      description: "Text and File upload for summary",
      enabled: true,
    },
    {
      id: "feature_1750602222121",
      name: "Regenerate Summary",
      description: "Regenerate Summary",
      enabled: true,
    },
    {
      id: "feature_1750602231452",
      name: "Unlimited cloud storage",
      description: "Unlimited cloud storage",
      enabled: true,
    },
    {
      id: "feature_1750602249423",
      name: "Multiple export formats (Copy, text, PDF, Word and JSON)",
      description: "Multiple export formats (Copy, text, PDF, Word and JSON)",
      enabled: true,
    },
    {
      id: "feature_1750602261923",
      name: "Advanced summarization styles",
      description: "Advanced summarization styles",
      enabled: true,
    },
    {
      id: "feature_1750602277441",
      name: "Custom AI Action",
      description: "Custom AI Action",
      enabled: true,
    },
    {
      id: "feature_1750602289679",
      name: "Convert the summary to any language",
      description: "Convert the summary to any language",
      enabled: true,
    },
    {
      id: "feature_1750602300042",
      name: "Offline capabilities",
      description: "Offline capabilities",
      enabled: true,
    },
  ],
};

// Legacy DEFAULT_LIMITS for backward compatibility (deprecated)
export const DEFAULT_LIMITS: { [planName: string]: LimitsConfiguration } = {
  "Free Plan": {
    storageGB: 2,

    minutesPerMonth: 300,
    summariesPerMonth: "unlimited",
    maxDurationMinutes: 30,
  },
  "Personal Plan": {
    storageGB: 20,

    minutesPerMonth: 3000,
    summariesPerMonth: "unlimited",
    maxDurationMinutes: 120,
  },
  "Premium Plan": {
    storageGB: "unlimited",

    minutesPerMonth: "unlimited",
    summariesPerMonth: "unlimited",
    maxDurationMinutes: "unlimited",
  },
};
